import { addDays, addMonths, addWeeks } from 'date-fns';

import { ReportScheduleItem } from '@futrli-sdk/report_scheduling';

const DAYS_IN_WEEK = 7;
type ScheduleWeekday = Exclude<ReportScheduleItem['weekday'], null>;

const WEEKDAY_INDEX: Record<ScheduleWeekday, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

/** Returns the first date on or after the given date that matches a weekday. */
const getFirstWeekdayOnOrAfter = (date: Date, targetDay: number): Date => {
  const daysToAdd = (targetDay - date.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  return daysToAdd === 0 ? date : addDays(date, daysToAdd);
};

/** Returns the number of days in the month containing the given date. */
const getLastDayOfMonth = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

/** Creates a date in the given month, clamping the day to that month's end. */
const getDateInMonth = (date: Date, dayOfMonth: number): Date => {
  const day = Math.min(dayOfMonth, getLastDayOfMonth(date));

  return new Date(date.getFullYear(), date.getMonth(), day);
};

/** Finds the first monthly occurrence on or after the supplied date. */
const getFirstMonthlyOccurrence = (date: Date, dayOfMonth: number): Date => {
  const occurrenceThisMonth = getDateInMonth(date, dayOfMonth);

  if (date.getDate() <= occurrenceThisMonth.getDate()) {
    return occurrenceThisMonth;
  }

  return getDateInMonth(addMonths(date, 1), dayOfMonth);
};

/** Calculates the final occurrence date for a monthly schedule. */
const getMonthlyEndDate = (
  firstOccurrence: Date,
  remainingOccurrences: number,
  frequency: number,
  dayOfMonth: number
): Date => getDateInMonth(addMonths(firstOccurrence, remainingOccurrences * frequency), dayOfMonth);

/** Parses a schedule start date as a local date without applying timezone conversion. */
const parseScheduleStartDate = (startDate: string): Date => {
  const [datePart] = startDate.split('T');
  const [year, month, day] = datePart.split('-').map(Number);

  return new Date(year, month - 1, day);
};

/**
 * Calculates the date of the last occurrence for a schedule with
 * `end_type: 'end-after-occurrences'`.
 */
export const calculateEndDateFromOccurrences = (schedule: ReportScheduleItem): Date | null => {
  if (!schedule.end_after_occurrences || schedule.end_after_occurrences < 1) {
    return null;
  }

  const start = parseScheduleStartDate(schedule.start_date);
  const remainingOccurrences = schedule.end_after_occurrences - 1;

  switch (schedule.period) {
    case 'daily':
      return addDays(start, remainingOccurrences * schedule.repeating_frequency);

    case 'weekly': {
      if (!schedule.weekday) {
        return null;
      }
      const firstOccurrence = getFirstWeekdayOnOrAfter(start, WEEKDAY_INDEX[schedule.weekday]);
      return addWeeks(firstOccurrence, remainingOccurrences * schedule.repeating_frequency);
    }

    case 'monthly': {
      if (schedule.day_of_month === null) {
        return null;
      }
      const firstOccurrence = getFirstMonthlyOccurrence(start, schedule.day_of_month);
      return getMonthlyEndDate(
        firstOccurrence,
        remainingOccurrences,
        schedule.repeating_frequency,
        schedule.day_of_month
      );
    }

    case 'do-not-repeat':
    default:
      return start;
  }
};
